/**
 * The phone's proposals screen (docs/01 §4).
 *
 * Order on the screen follows what a person can actually do about each thing:
 *
 *  1. **Pending**, with anything waiting on your tap first. Every card carries
 *     its own countdown, or — for money — the lock that says there is no
 *     countdown and never will be.
 *  2. **Rejected with an alternative**, but only yours. This is the piece that
 *     closes the loop: a no came back with "after 4pm works", and rather than
 *     leaving you to remember it, the suggestion sits here as a one-tap
 *     "Raise this instead" that creates a NEW proposal linked to the old one.
 *  3. **The decision log**, collapsed.
 *
 * Voting is genuinely authenticated work (docs/02 §8: "anything that must be
 * genuinely authenticated comes from a phone"), which is why the three-way vote
 * lives here and not on the wall tablet.
 *
 * Section headers stick as the list scrolls, so you always know which of the
 * three you are looking at.
 */

import { useCallback, useState } from 'react'
import type { OfficeDb } from '../../lib/db.js'
import type { MemberId, TrustedClock } from '@office/domain'
import { ProposalCard } from './ProposalCard.js'
import { DecisionLog } from './DecisionLog.js'
import { VoteSheet, type RejectMode } from './VoteSheet.js'
import { describeCounter, useProposalBoard, type ProposalView } from './proposal-data.js'
import '../chores/phone.css'
import './proposals.css'

export interface ProposalsScreenProps {
  db: OfficeDb
  /** Office time. Nothing in this tree reads the ambient device clock. */
  clock: TrustedClock
  me: MemberId
  /** Clock refresh interval; 0 freezes it for a deterministic test. */
  nowIntervalMs?: number | undefined
}

interface Rejecting {
  view: ProposalView
  mode: RejectMode
}

export function ProposalsScreen({ db, clock, me, nowIntervalMs }: ProposalsScreenProps) {
  const { board, actions } = useProposalBoard(db, clock, me, { nowIntervalMs })
  const [rejecting, setRejecting] = useState<Rejecting | null>(null)

  const closeSheet = useCallback(() => setRejecting(null), [])

  const submitAlternative = useCallback(
    (suggestion: string, note: string) => {
      const current = rejecting
      setRejecting(null)
      if (current !== null) void actions.rejectWithAlternative(current.view, suggestion, note)
    },
    [actions, rejecting],
  )

  const submitReason = useCallback(
    (reason: string) => {
      const current = rejecting
      setRejecting(null)
      if (current !== null) void actions.rejectWithReason(current.view, reason)
    },
    [actions, rejecting],
  )

  const waitingOnMe = board.pending.filter(v => v.awaitingMe).length

  return (
    <section className="ph-screen" aria-labelledby="proposals-title">
      <header className="ph-head">
        <p className="ph-eyebrow" data-tone={waitingOnMe === 0 ? 'calm' : 'action'}>
          {waitingOnMe === 0
            ? 'Nothing needs your vote'
            : `${waitingOnMe} waiting on you`}
        </p>
        <h1 id="proposals-title" className="ph-title">
          Decisions
        </h1>
        <p className="ph-sub">
          Unanimous, and any single no settles it. Money never approves itself.
        </p>
      </header>

      <section className="ph-section" aria-labelledby="pending-title">
        <div className="ph-section-head">
          <h2 id="pending-title" className="ph-section-title">
            Pending
          </h2>
          {board.pending.length > 0 ? (
            <span className="ph-count">{board.pending.length}</span>
          ) : null}
        </div>
        {board.pending.length === 0 ? (
          <div className="ph-empty">
            <span className="ph-empty-mark" aria-hidden="true">
              ✓
            </span>
            <p className="ph-empty-line">Nothing is waiting on a decision.</p>
            <p className="ph-empty-hint">
              Raise one from the + button — a guest, a purchase, time off, or anything else.
            </p>
          </div>
        ) : (
          <ul className="ph-list">
            {board.pending.map(v => (
              <ProposalCard
                key={String(v.proposal.id)}
                view={v}
                members={board.members}
                me={me}
                now={board.now}
                onApprove={view => void actions.approve(view)}
                onCounter={view => setRejecting({ view, mode: 'alternative' })}
                onExplain={view => setRejecting({ view, mode: 'reason' })}
                onCancel={view => void actions.cancel(view)}
              />
            ))}
          </ul>
        )}
      </section>

      {board.relaunchable.length > 0 ? (
        <section className="ph-section" aria-labelledby="alternatives-title">
          <div className="ph-section-head">
            <h2 id="alternatives-title" className="ph-section-title">
              Turned down, but with an alternative
            </h2>
          </div>
          <ul className="ph-list">
            {board.relaunchable.map(r => (
              <li
                key={String(r.view.proposal.id)}
                className="card stack gap-2 pr-relaunch"
                data-testid="relaunch"
              >
                <p className="pr-relaunch-line">
                  <strong>{r.view.proposal.title}</strong> was turned down.
                </p>
                <p className="pr-relaunch-line">
                  {r.counterFrom} suggested: <strong>{describeCounter(r.counter)}</strong>
                </p>
                <p className="pr-relaunch-note">
                  Raising it starts a new proposal linked to this one. The original stays in the
                  log exactly as it was.
                </p>
                <button
                  type="button"
                  className="btn btn-primary ph-inline"
                  onClick={() => void actions.raise(r.view, r.counter)}
                >
                  Raise this instead
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <DecisionLog
        decided={board.decided}
        log={board.log}
        members={board.members}
        me={me}
        rolloverHour={board.rolloverHour}
      />

      {rejecting !== null ? (
        <VoteSheet
          mode={rejecting.mode}
          proposalTitle={rejecting.view.proposal.title}
          onAlternative={submitAlternative}
          onReason={submitReason}
          onClose={closeSheet}
        />
      ) : null}
    </section>
  )
}
